import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Award, Factory, FlaskConical, Shield, Zap, Cpu, Thermometer, Target, ArrowRight, CheckCircle } from 'lucide-react';

const firsts = [
  "Pioneered the introduction of Screw Compressor-based chillers in the South Indian pharmaceutical industry.",
  "Engineered and deployed multiple high-capacity screw compressor chiller systems for demanding process applications.",
  "First to integrate Variable Frequency Drive (VFD) technology in industrial chillers for energy optimization.",
  "Introduced Electronic Expansion Valve (EEV) technology to the Indian market.",
  "First to introduce SS304 evaporator systems in the pharmaceutical sector — a standard subsequently adopted by major branded manufacturers.",
  "Successfully engineered advanced Cascade Chiller Systems capable of achieving ultra-low temperatures up to −70°C.",
  "Introduced several global component brands from Italy and China into India, strengthening domestic industrial capabilities.",
  "Continuously identify and implement emerging global refrigeration technologies within the Indian industrial landscape.",
];

const capabilities = [
  { icon: Cpu, title: "Multi-Screw Compressor Configurations", desc: "Complex multi-compressor staging for large industrial plants with redundancy and part-load efficiency." },
  { icon: Thermometer, title: "Cascade Refrigeration Systems", desc: "Ultra-low temperature applications down to −70°C using HT + LT stage cascade architecture." },
  { icon: FlaskConical, title: "Pharma-Compliant SS304 Evaporators", desc: "GMP-ready evaporator designs with SS304/SS316L wetted surfaces and full IQ/OQ documentation." },
  { icon: Zap, title: "Energy-Efficient Process Cooling", desc: "VFD integration, economizer technology, and advanced PID controls for minimum energy consumption." },
  { icon: Target, title: "Fully Customized Solutions", desc: "Every system is engineered to client-specific process requirements — no off-the-shelf compromises." },
  { icon: Shield, title: "Rigorous Validation & Testing", desc: "Detailed performance engineering, site-condition analysis, and application-specific validation before deployment." },
];

export default function About() {
  React.useEffect(() => {
    document.title = 'Why Choose Us | Laminate Chillers – 40+ Years Industrial Chiller Leadership';
  }, []);
  return (
    <div className="page-transition">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--brand-navy) 0%, #0f2847 60%, #0A1628 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 40%, var(--brand-teal), transparent 50%)' }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative z-10">
          <Badge className="mb-4 text-white border-teal-500/30" style={{ background: 'rgba(14,138,138,0.2)' }}>Why Choose Us</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Engineering Innovation Before<br />
            <span style={{ color: 'var(--brand-teal-2)' }}>the Market Knew It Needed It</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">Four decades of industrial cooling leadership. We do not manufacture standard equipment — we engineer precision cooling systems tailored exclusively to client-specific process requirements.</p>
        </div>
      </section>

      {/* Legacy stats */}
      <section className="border-b" style={{ background: 'var(--surface-1)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '40+', label: 'Years of Leadership' },
              { value: 'Pioneers', label: 'Screw Chillers in South India' },
              { value: '−70°C', label: 'Ultra-Low Temp Capability' },
              { value: 'Custom', label: 'Every System Engineered' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold" style={{ color: 'var(--brand-teal)', fontFamily: 'Space Grotesk' }}>{s.value}</div>
                <div className="text-sm text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-14" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Four Decades of Industrial Cooling Leadership</h2>
            <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
              <p>For over 40 years, we have been defining the evolution of industrial chiller technology in India. As a custom-engineered chiller manufacturing company, our foundation is built on technical depth, innovation leadership, and uncompromising engineering standards.</p>
              <p className="font-medium" style={{ color: 'var(--brand-navy)' }}>We do not manufacture standard equipment. We engineer precision cooling systems tailored exclusively to client-specific process requirements.</p>
              <p>Our legacy is not built on volume — it is built on pioneering advancements.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Firsts */}
      <section className="py-14" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(14,138,138,0.1)' }}>
              <Award className="w-5 h-5" style={{ color: 'var(--brand-teal)' }} />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Industry Firsts That Redefined Standards</h2>
              <p className="text-sm text-muted-foreground">Technologies we introduced that later became industry benchmarks</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {firsts.map((item, i) => (
              <Card key={i} style={{ background: 'var(--surface-1)' }}>
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(14,138,138,0.1)' }}>
                    <CheckCircle className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} />
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-6 p-4 rounded-lg border-l-4" style={{ borderColor: 'var(--brand-teal)', background: 'rgba(14,138,138,0.04)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>We have never waited for change. We have consistently led it.</p>
          </div>
        </div>
      </section>

      {/* Advanced Capabilities */}
      <section className="py-14" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Advanced Engineering & Technical Capabilities</h2>
          <p className="text-sm text-muted-foreground mb-8">Our expertise extends across complex industrial refrigeration architectures</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((cap, i) => (
              <Card key={i} style={{ background: 'var(--surface-1)' }}>
                <CardContent className="p-5">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: 'rgba(14,138,138,0.1)' }}>
                    <cap.icon className="w-4.5 h-4.5" style={{ color: 'var(--brand-teal)' }} />
                  </div>
                  <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>{cap.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{cap.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4 italic">Each system undergoes detailed performance engineering, site-condition analysis, and application-specific validation before deployment.</p>
        </div>
      </section>

      {/* Engineering Philosophy */}
      <section className="py-14" style={{ background: 'var(--brand-navy)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>Our Engineering Philosophy</h2>
            <div className="space-y-4 text-slate-300 text-sm leading-relaxed">
              <div className="flex justify-center gap-6 mb-6">
                {['Not Traders', 'Not Assemblers', 'Engineering Solution Architects'].map((label, i) => (
                  <div key={i} className="px-4 py-2 rounded-lg border" style={{ borderColor: i === 2 ? 'var(--brand-teal)' : 'rgba(255,255,255,0.15)', background: i === 2 ? 'rgba(14,138,138,0.15)' : 'transparent' }}>
                    <span className={`text-sm font-medium ${i === 2 ? 'text-[var(--brand-teal-2)]' : 'text-slate-400 line-through'}`}>{label}</span>
                  </div>
                ))}
              </div>
              <p>Our strength lies in deep, on-ground technical development and practical industrial execution. Every solution is backed by decades of hands-on engineering experience and process understanding.</p>
              <p className="font-medium text-white">We prioritize performance, reliability, and long-term operational stability over mass production.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Selective Portfolio */}
      <section className="py-14" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Selective Portfolio. Superior Standards.</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">We remain highly selective in our product range, focusing only on systems where we can deliver measurable technical value.</p>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">Our solutions are built for:</p>
              <div className="space-y-3">
                {[
                  'Precision temperature control',
                  'High operational efficiency',
                  'Process-critical reliability',
                  'Long service life under demanding industrial conditions',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(14,138,138,0.1)' }}>
                      <CheckCircle className="w-3.5 h-3.5" style={{ color: 'var(--brand-teal)' }} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img src="https://images.unsplash.com/photo-1717386255785-59c670564341?w=800" alt="Engineering facility" className="w-full h-full object-cover rounded-lg" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-14" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Our Commitment</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">Our commitment remains steadfast — to engineer advanced, efficient, and future-ready cooling systems that support industrial growth and innovation.</p>
            <p className="text-sm font-medium leading-relaxed mb-8" style={{ color: 'var(--brand-navy)' }}>With four decades of technical leadership behind us, we continue to shape the next generation of industrial refrigeration in India.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/products">
                <Button className="text-white" style={{ background: 'var(--brand-teal)' }}>
                  Explore Our Products <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/products/custom">
                <Button variant="outline">Submit Custom Requirements</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-6 border-t" style={{ background: 'var(--surface-1)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-center gap-6">
            {['40+ Years Experience', 'ISO 9001:2015', 'CE Marked', 'ASME U-Stamp', 'PED Compliant', 'Eurovent Tested'].map((cert) => (
              <Badge key={cert} variant="outline" className="text-xs py-1.5 px-3">{cert}</Badge>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
